import type { CellValue, ColumnDef } from "@/lib/types";

/** Encode a form field's raw input into the API cell value for its column type.
 * Shared by the ticket SubmitForm and the board AddRowDialog. Throws a German
 * message on invalid number / URL so the caller can surface a field error. */
export function encodeForApi(column: ColumnDef, raw: unknown): CellValue {
  switch (column.type) {
    case "text":
    case "long_text":
    case "status":
    case "date":
      return raw as CellValue;
    case "checkbox":
      return Boolean(raw) as CellValue;
    case "person":
      return (Array.isArray(raw) ? raw : []) as CellValue;
    case "file":
      // Already a FileRef[] — uploaded before submit, so there is nothing to
      // parse. The backend re-validates that each entry carries a string `key`.
      return (Array.isArray(raw) ? raw : []) as CellValue;
    case "number": {
      const n = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isFinite(n)) throw new Error("Bitte eine gültige Zahl eingeben.");
      return n;
    }
    case "tags": {
      const parts = String(raw)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      return parts as CellValue;
    }
    case "link": {
      const v = raw as { url?: string; label?: string };
      if (!v.url) throw new Error("URL fehlt.");
      try {
        new URL(v.url);
      } catch {
        throw new Error("Ungültige URL.");
      }
      return { url: v.url, label: v.label ?? v.url } as CellValue;
    }
    default:
      return raw as CellValue;
  }
}
